import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import useThemeColor from "../../hooks/useThemeColor";
import "./UpdateCompany.css";

const normalizeSettings = (data) => ({
  useCustomSender: data?.useCustomSender ?? data?.UseCustomSender ?? false,
  senderEmail: data?.senderEmail ?? data?.SenderEmail ?? "",
  senderDisplayName: data?.senderDisplayName ?? data?.SenderDisplayName ?? "",
  replyToEmail: data?.replyToEmail ?? data?.ReplyToEmail ?? "",
  sesVerificationStatus: data?.sesVerificationStatus ?? data?.SesVerificationStatus ?? "Pending",
  verifiedAt: data?.verifiedAt ?? data?.VerifiedAt ?? null,
});

const CompanyEmailSender = ({ CompID }) => {
  const themeColor = useThemeColor();
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [settings, setSettings] = useState(normalizeSettings({}));

  const loadSettings = useCallback(async () => {
    if (!CompID) return;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.SA_GET_COMPANY_EMAIL_SETTINGS, {
        params: { CompID },
      });
      const data = response.data?.data ?? response.data?.Data ?? {};
      setSettings(normalizeSettings(data));
    } catch {
      showErrorToast("Failed to load email sender settings.");
      setSettings(normalizeSettings({}));
    } finally {
      setLoading(false);
    }
  }, [CompID]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleMarkVerified = async () => {
    if (!CompID) return;

    try {
      setMarking(true);
      const response = await axiosInstance.post(
        `${APIs.SA_MARK_COMPANY_EMAIL_SETTINGS_VERIFIED}?CompID=${CompID}`
      );

      if (!response.data?.status) {
        showErrorToast(response.data?.message || "Could not mark sender as verified.");
        return;
      }

      showSuccessToast(response.data?.message || "Email sender marked as verified.");
      await loadSettings();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to mark email sender as verified.");
    } finally {
      setMarking(false);
    }
  };

  if (loading) {
    return <Preloader />;
  }

  const isVerified = settings.sesVerificationStatus === "Verified";
  const isPending = settings.useCustomSender && settings.senderEmail && !isVerified;

  const statusBadgeClass = isVerified
    ? "uc-badge-pill text-success"
    : isPending
      ? "uc-badge-pill text-warning"
      : "uc-badge-pill text-secondary";

  const statusBadgeStyle = isVerified
    ? { background: "rgba(25, 135, 84, 0.12)" }
    : isPending
      ? { background: "rgba(255, 193, 7, 0.18)" }
      : { background: "rgba(108, 117, 125, 0.12)" };

  const statusText = isVerified
    ? "Verified in AWS SES"
    : isPending
      ? "Pending AWS verification"
      : "Not configured — using ServeIQ default";

  return (
    <section className="uc-section uc-section--nested">
      <div className="uc-section__header">
        <h5>Email Sender</h5>
        <p>
          Add the sender email in AWS SES (Verified identities), then mark it verified here after
          the restaurant completes AWS verification.
        </p>
      </div>
      <div className="uc-section__body">
        <div className="uc-status-banner">
          <span className={statusBadgeClass} style={statusBadgeStyle}>
            {statusText}
          </span>
          <span className="uc-status-banner__text text-muted">
            SES verification status for this company&apos;s custom sender
          </span>
        </div>

        <div className="uc-meta-grid mb-4">
          <div className="uc-meta-item">
            <span className="uc-meta-item__label">Custom sender enabled</span>
            <div className="uc-meta-item__value">
              {settings.useCustomSender ? "Yes" : "No"}
            </div>
          </div>
          <div className="uc-meta-item">
            <span className="uc-meta-item__label">Sender email</span>
            <div className="uc-meta-item__value">{settings.senderEmail || "—"}</div>
          </div>
          <div className="uc-meta-item">
            <span className="uc-meta-item__label">Display name</span>
            <div className="uc-meta-item__value">{settings.senderDisplayName || "—"}</div>
          </div>
          <div className="uc-meta-item">
            <span className="uc-meta-item__label">Reply-To</span>
            <div className="uc-meta-item__value">
              {settings.replyToEmail || settings.senderEmail || "—"}
            </div>
          </div>
          {settings.verifiedAt && (
            <div className="uc-meta-item">
              <span className="uc-meta-item__label">Verified at</span>
              <div className="uc-meta-item__value">
                {new Date(settings.verifiedAt).toLocaleString()}
              </div>
            </div>
          )}
        </div>

        {settings.useCustomSender &&
          settings.senderEmail &&
          settings.sesVerificationStatus !== "Verified" && (
            <button
              type="button"
              className="uc-btn-primary"
              onClick={handleMarkVerified}
              disabled={marking}
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                boxShadow: `0 2px 8px ${themeColor}40`,
              }}
            >
              <i className="fa fa-check-circle"></i>
              {marking ? "Updating…" : "Mark as verified in SES"}
            </button>
          )}
      </div>
    </section>
  );
};

export default CompanyEmailSender;
